<template>
  <div class="np" data-test="pfe-new-patients-body">
    <div class="np-top">
      <send-switch-card
        test-id="pfe-np"
        title="Send new patient forms"
        :state="switchView"
        :busy="saving"
        :error="switchError"
        :confirm-open="confirmOpen"
        confirm-title="Turn on new patient forms?"
        :confirm-body="confirmBody"
        @request-on="onRequestOn"
        @turn-off="onTurnOff"
        @confirm="onConfirm"
        @cancel="confirmOpen = false"
      />
      <ul v-if="serverProblems.length" class="np-problems" role="alert" data-test="pfe-np-server-problems">
        <li v-for="(p, i) in serverProblems" :key="i">{{ p }}</li>
      </ul>
      <!-- A workflow already sends new patients their forms (D15b): say so; the messages below stay editable but are marked "Not sending". -->
      <div v-if="workflowSends" class="np-info" role="status" data-test="pfe-np-workflow-notice">
        <i class="fa fa-info-circle" aria-hidden="true"></i>
        <div v-if="server.enabled === 'TRUE'">New patients get their forms from the workflow <strong>‘{{ workflowName }}’</strong> while it’s on, so nothing is sent from here. Turn that workflow off and this starts sending again on its own — Teamio never sends both.</div>
        <div v-else>New patients already get their forms from the workflow <strong>‘{{ workflowName }}’</strong> when an appointment is scheduled. Turn that workflow off first, then turn this on — Teamio never sends both.</div>
      </div>
    </div>

    <div class="np-cols">
      <div class="np-left">
        <section class="np-card" aria-labelledby="np-who-title">
          <span id="np-who-title" class="np-card-title">Who gets it</span>
          <div class="np-seg" role="radiogroup" aria-label="Who gets it">
            <button
              v-for="m in modes"
              :key="m.value"
              type="button"
              role="radio"
              class="np-seg-btn"
              :class="{ 'np-seg-btn--on': work.mode === m.value }"
              :aria-checked="work.mode === m.value ? 'true' : 'false'"
              :data-test="'pfe-np-mode-' + m.value"
              @click="setMode(m.value)"
            >{{ m.label }}</button>
          </div>
          <p class="np-help" data-test="pfe-np-who-help">A new patient is someone with no completed treatment in Open Dental whose appointment is today or later. Each patient gets these forms once.</p>
        </section>

        <section class="np-card" aria-labelledby="np-when-title">
          <span id="np-when-title" class="np-card-title">When to send</span>
          <div class="np-when" data-test="pfe-np-when"><i class="fa fa-clock-o" aria-hidden="true"></i>Right after the appointment is booked.</div>
        </section>

        <div class="np-msg-head">
          <span class="np-card-title">Messages</span>
          <span class="np-sub">Each message needs a Form link.</span>
        </div>

        <!-- ALL NEW PATIENTS: one text + one email -->
        <div v-if="work.mode === 'all'" class="np-cards" data-test="pfe-np-everyone-cards">
          <forms-message-card
            v-for="ch in channels"
            :key="'all-' + ch"
            :test-id="'pfe-np-everyone-' + ch"
            :slot-key="'np-everyone-' + ch"
            :channel="ch"
            :value="cardOf(work.everyone, ch)"
            :default-text="defaultText(ch)"
            :default-subject="defaultSubject"
            :forms="forms"
            :packets="packets"
            :catalog-failed="catalogFailed"
            :site="site"
            :dim="notSending"
            @input="onCard('everyone', ch, $event)"
          />
        </div>

        <!-- SPECIFIC PROCEDURES: rule cards, then the always-present "Everyone else" -->
        <div v-else class="np-rules" data-test="pfe-np-rules">
          <div
            v-for="(rule, i) in work.rules"
            :key="rule.id"
            class="np-rule"
            :class="{ 'np-rule--open': isOpen(rule.id) }"
            data-test="pfe-np-rule"
            :data-rule-id="rule.id"
          >
            <div v-if="!isOpen(rule.id)" class="np-rule-row" data-test="pfe-np-rule-summary">
              <button type="button" class="np-rule-sum" aria-expanded="false" data-test="pfe-np-rule-toggle" @click="toggleRule(rule.id)">
                <i class="fa fa-chevron-right np-rule-chev" aria-hidden="true"></i>
                <span class="np-rule-label" data-test="pfe-np-rule-sum-label">{{ summaryOf(rule).label }}</span>
                <span class="np-dot" aria-hidden="true">&middot;</span><span class="np-rule-part" data-test="pfe-np-rule-sum-codes">{{ summaryOf(rule).codes }}</span>
                <span class="np-dot" aria-hidden="true">&middot;</span><span class="np-rule-part" data-test="pfe-np-rule-sum-providers">{{ summaryOf(rule).providers }}</span>
                <span class="np-dot" aria-hidden="true">&middot;</span><span class="np-rule-part" data-test="pfe-np-rule-sum-channels">{{ summaryOf(rule).channels }}</span>
              </button>
              <span v-if="ruleIssues(i) > 0" class="np-rule-issue" data-test="pfe-np-rule-issue">Needs attention</span>
              <button type="button" class="np-link" data-test="pfe-np-rule-edit" @click="toggleRule(rule.id)">Edit</button>
              <button type="button" class="np-icon-btn" aria-label="Remove rule" data-test="pfe-np-rule-remove" @click="removeRule(rule.id)"><i class="fa fa-trash-o" aria-hidden="true"></i></button>
            </div>

            <template v-else>
              <div class="np-rule-head">
                <input
                  class="np-rule-name"
                  type="text"
                  maxlength="100"
                  placeholder="Name this rule"
                  aria-label="Rule name"
                  :value="rule.label"
                  data-test="pfe-np-rule-label"
                  @input="setRuleField(rule.id, 'label', $event.target.value)"
                />
                <button type="button" class="np-link" aria-expanded="true" data-test="pfe-np-rule-collapse" @click="toggleRule(rule.id)"><i class="fa fa-chevron-up" aria-hidden="true"></i> Collapse</button>
                <button type="button" class="np-icon-btn" aria-label="Remove rule" data-test="pfe-np-rule-remove" @click="removeRule(rule.id)"><i class="fa fa-trash-o" aria-hidden="true"></i></button>
              </div>
              <div class="np-rule-fields">
                <div class="np-field">
                  <span class="np-field-label">Procedure codes</span>
                  <code-picker
                    :value="rule.codes"
                    :options="codeOptions"
                    :loading="codesLoading"
                    :initial-open="openCodesId === rule.id"
                    @input="setRuleField(rule.id, 'codes', $event)"
                  />
                  <div v-if="rule.codes.length === 0" class="np-error" role="alert" data-test="pfe-np-rule-error-codes">
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ noCodesText }}
                  </div>
                </div>
                <div class="np-field">
                  <span class="np-field-label">Providers</span>
                  <check-picker
                    :value="rule.providerNums"
                    :options="providerOpts"
                    :fallback-names="providerFallbacks(rule)"
                    display="text"
                    placeholder="Any provider"
                    search-placeholder="Search providers"
                    aria-label="Providers"
                    empty-text="No providers to pick."
                    test-id="pfe-np-providers"
                    @input="setRuleField(rule.id, 'providerNums', $event)"
                  />
                  <span class="np-field-help">Leave empty for any provider.</span>
                </div>
              </div>
              <div v-if="noChannelFor(rule)" class="np-error" role="alert" data-test="pfe-np-rule-error-channels">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>{{ noChannelText }}
              </div>
              <div class="np-cards">
                <forms-message-card
                  v-for="ch in channels"
                  :key="rule.id + '-' + ch"
                  :test-id="'pfe-np-rule-' + ch"
                  :slot-key="'np-' + rule.id + '-' + ch"
                  :channel="ch"
                  :value="cardOf(rule, ch)"
                  :default-text="defaultText(ch)"
                  :default-subject="defaultSubject"
                  :forms="forms"
                  :packets="packets"
                  :catalog-failed="catalogFailed"
                  :site="site"
                  :dim="notSending"
                  @input="onCard(rule.id, ch, $event)"
                />
              </div>
            </template>
          </div>

          <div class="np-add">
            <button type="button" class="np-btn" :disabled="work.rules.length >= maxRules" data-test="pfe-np-add-rule" @click="addRule">
              <i class="fa fa-plus" aria-hidden="true"></i> Add rule
            </button>
          </div>

          <div class="np-else" data-test="pfe-np-everyone-else">
            <div class="np-else-head">
              <span class="np-card-title">Everyone else</span>
              <span class="np-sub">New patients whose appointment matches none of the rules above get this message.</span>
            </div>
            <div class="np-cards">
              <forms-message-card
                v-for="ch in channels"
                :key="'else-' + ch"
                :test-id="'pfe-np-everyone-' + ch"
                :slot-key="'np-everyone-' + ch"
                :channel="ch"
                :value="cardOf(work.everyone, ch)"
                :default-text="defaultText(ch)"
                :default-subject="defaultSubject"
                :forms="forms"
                :packets="packets"
                :catalog-failed="catalogFailed"
                :site="site"
                :dim="notSending"
                @input="onCard('everyone', ch, $event)"
              />
            </div>
          </div>
        </div>
      </div>

      <forms-preview-pane
        test-id="pfe-np-preview"
        :channel="previewChannel"
        :message="previewMessage"
        :site="site"
        :forms="forms"
        :packets="packets"
        :sample="sample"
        apt-word="first visit"
        :picks="previewPicks"
        :pick="previewHolderKey"
        @update:channel="previewChannel = $event"
        @update:pick="previewKey = $event"
      />
    </div>
  </div>
</template>

<script>
import SendSwitchCard from './SendSwitchCard.vue';
import FormsMessageCard from './FormsMessageCard.vue';
import FormsPreviewPane from './FormsPreviewPane.vue';
import CodePicker from './CodePicker.vue';
import CheckPicker from './CheckPicker.vue';
import tabMixin from './formsSettingsTab';
import { saveFormsSettingsSection } from '@/api/formsSettings';
import { chipsForCodes } from '@/utils/postOp/rows';
import { isOn } from '@/utils/formsSettings/formLink';
import {
  NEW_PATIENT_DEFAULT_SMS_BODY, NEW_PATIENT_DEFAULT_EMAIL_SUBJECT, NEW_PATIENT_DEFAULT_EMAIL_BODY, NEW_PATIENT_PROBLEM_TEXT,
  validateNewPatientSettings, newPatientBlockReasons, newRule,
} from '@/utils/formsSettings/newPatient';
import {
  newPatientEditable, newPatientBlob, newPatientSavePayload, newPatientSignature, newPatientSwitchReasons, sendSwitchState, channelWords,
  toCardMessage, fromCardMessage, nextRuleId, providerOptions, ruleSummary,
} from '@/utils/formsSettings/editorModel';

const MAX_RULES = 50; // the server's limit (lib/formsSettings/newPatientSettings.js)
const clone = (v) => JSON.parse(JSON.stringify(v));
const flags = (settings) => ({
  enabled: settings && isOn(settings.enabled) ? 'TRUE' : 'FALSE',
  liveSince: settings && typeof settings.liveSince === 'string' && settings.liveSince ? settings.liveSince : null,
});

/**
 * Forms Settings → New Patients (plan docs/plans/2026-10-20-forms-settings-plan.md §0 D4 D6 D15, §1f, §2.1; board frames 1a–1f).
 *
 *   the switch card  "Send new patient forms" — Off / On since … / Blocked (the FIRST reason). Reasons: the server's sticky ones (a workflow already
 *                    sends, the several-clinics block, a failed lookup), then WP0 newPatientBlockReasons over the WORKING copy. Confirm sends the
 *                    content (when edited) + enabled:'TRUE' in ONE request — the only place `enabled` is ever sent; turn-off sends { enabled:'FALSE' }.
 *   Who gets it      All new patients (one Text + one Email) | Specific procedures (rule cards + the always-present "Everyone else")
 *   When to send     right after the appointment is booked (the "remind again" option is not built)
 *   Preview          the post-op tab's MessagePreview, through FormsPreviewPane
 *
 * THE HOST CONTRACT (src/utils/formsSettings/host.js, mixin ./formsSettingsTab): `dirty` is true only after the office edited THIS tab (the WP0 defaults it
 * opens on never dirty it — D15a); `errorCount` is WP0 validateNewPatientSettings over the working copy; save() sends section 'newPatient' with the
 * content fields ONLY (never `enabled` / `liveSince`); reset() puts back what the server has.
 *
 * Every validation, default and block reason comes from the WP0 files; this component never re-implements one.
 */
export default {
  name: 'PfeNewPatientsTab',
  tabKey: 'newPatients',
  components: { SendSwitchCard, FormsMessageCard, FormsPreviewPane, CodePicker, CheckPicker },
  mixins: [tabMixin],
  props: {
    /** buildCodeOptions(procCodes) from the host (it loads the catalog once), for the rule cards' code picker. */
    codeOptions: { type: Array, default: () => [] },
    codesLoading: { type: Boolean, default: false },
  },
  data() {
    const editable = newPatientEditable(this.context && this.context.settings);
    return {
      channels: ['sms', 'email'],
      modes: [{ value: 'all', label: 'All new patients' }, { value: 'rules', label: 'Specific procedures' }],
      maxRules: MAX_RULES,
      defaultSubject: NEW_PATIENT_DEFAULT_EMAIL_SUBJECT,
      noCodesText: NEW_PATIENT_PROBLEM_TEXT.RULE_NO_CODES,
      noChannelText: NEW_PATIENT_PROBLEM_TEXT.RULE_NO_CHANNEL,

      work: clone(editable),          // what the office is editing
      baseline: clone(editable),      // what the server has (reset() restores it)
      baselineSig: newPatientSignature(editable),
      server: flags(this.context && this.context.settings), // the switch as the server holds it: { enabled:'TRUE'|'FALSE', liveSince }

      confirmOpen: false,
      expanded: {},                   // rule id → true (a rule card is collapsed to its summary row until opened)
      openCodesId: null,              // the rule whose code menu opens when it mounts ("+ Add rule")
      previewChannel: 'sms',
      previewKey: 'everyone',
    };
  },
  computed: {
    // ── the host contract ──
    dirty() { return newPatientSignature(this.work) !== this.baselineSig; },
    problems() { return validateNewPatientSettings(newPatientBlob(this.work, this.server.enabled)); },
    errorCount() { return this.problems.length; },

    // ── the switch card ──
    workflowNames() {
      const list = this.context && this.context.readiness && Array.isArray(this.context.readiness.workflows) ? this.context.readiness.workflows : [];
      return list.map((w) => (w && typeof w.name === 'string' ? w.name : ''));
    },
    liveReasons() {
      return newPatientBlockReasons(newPatientBlob(this.work, this.server.enabled), {
        activeWorkflowNames: this.workflowNames,
        smsReady: this.smsReady,
        emailReady: this.emailReady,
      });
    },
    reasons() {
      return newPatientSwitchReasons(this.context && this.context.blockReasons, this.liveReasons);
    },
    workflowSends() { return this.reasons.some((r) => r.code === 'WORKFLOW_SENDS'); },
    notSending() { return this.workflowSends; },
    workflowName() {
      const first = this.workflowNames.find((n) => n && n.trim());
      return first ? first.trim() : 'Untitled workflow';
    },
    switchView() {
      return sendSwitchState({
        enabled: this.server.enabled === 'TRUE',
        liveSinceText: this.liveSinceTextFor(this.server.liveSince),
        reasons: this.reasons,
        offMessage: 'Off — new patients aren’t sent their forms automatically.',
        onMessage: 'On since {since}. Only appointments booked after this time are sent.',
        onMessageNoDate: 'On. Only appointments booked after you turned it on are sent.',
        // ON while an active workflow sends new patients their forms: the trigger skips every booking (D15c) — say "paused", not a lock.
        paused: { codes: ['WORKFLOW_SENDS'], message: `Paused — the workflow ‘${this.workflowName}’ is sending new patients their forms, so nothing is sent from here.` },
      });
    },
    scopeCards() {
      return [this.work.everyone].concat(this.work.mode === 'rules' ? this.work.rules : []);
    },
    confirmBody() {
      const sms = this.scopeCards.some((c) => isOn(c.sms.on));
      const email = this.scopeCards.some((c) => isOn(c.email.on));
      return `${channelWords(sms, email) || 'Messages'} will send right after a new patient’s appointment is booked.`;
    },

    // ── rule cards ──
    providerOpts() { return providerOptions(this.$store && this.$store.state.customer && this.$store.state.customer.providers); },

    // ── preview ──
    previewPicks() {
      if (this.work.mode !== 'rules') return [];
      return [{ key: 'everyone', label: 'Everyone else' }].concat(this.work.rules.map((r) => ({ key: r.id, label: r.label.trim() || 'Untitled rule' })));
    },
    previewHolderKey() {
      return this.work.mode === 'rules' && this.work.rules.some((r) => r.id === this.previewKey) ? this.previewKey : 'everyone';
    },
    previewHolder() {
      return this.previewHolderKey === 'everyone' ? this.work.everyone : this.work.rules.find((r) => r.id === this.previewHolderKey);
    },
    previewMessage() { return toCardMessage(this.previewHolder[this.previewChannel], this.previewChannel); },
  },
  watch: {
    // the context is handed down once the host has it; a fresh one (a retry) replaces an UNEDITED tab
    context(next, prev) {
      if (next === prev || this.dirty || this.saving) return;
      this.adopt(next && next.settings);
    },
  },
  methods: {
    // ── what the cards show ──
    cardOf(holder, channel) { return toCardMessage(holder[channel], channel); },
    defaultText(channel) { return channel === 'email' ? NEW_PATIENT_DEFAULT_EMAIL_BODY : NEW_PATIENT_DEFAULT_SMS_BODY; },

    // ── editing ──
    onCard(target, channel, card) {
      this.clearFailure();
      const next = fromCardMessage(card, channel);
      if (target === 'everyone') { this.$set(this.work.everyone, channel, next); return; }
      const rule = this.work.rules.find((r) => r.id === target);
      if (rule) this.$set(rule, channel, next);
    },
    setMode(mode) {
      if (this.work.mode === mode) return;
      this.clearFailure();
      this.work.mode = mode;
    },
    addRule() {
      if (this.work.rules.length >= MAX_RULES) return;
      this.clearFailure();
      const rule = newRule(nextRuleId(this.work.rules));
      this.work.rules.push(rule);
      this.$set(this.expanded, rule.id, true); // a new rule opens expanded, with its code menu open
      this.openCodesId = rule.id;
    },
    removeRule(id) {
      this.clearFailure();
      this.work.rules = this.work.rules.filter((r) => r.id !== id);
      this.$delete(this.expanded, id);
    },
    setRuleField(id, field, value) {
      this.clearFailure();
      const rule = this.work.rules.find((r) => r.id === id);
      if (rule) this.$set(rule, field, value);
    },
    isOpen(id) { return this.expanded[id] === true; },
    toggleRule(id) {
      this.openCodesId = null;
      this.$set(this.expanded, id, !this.isOpen(id));
    },
    ruleIssues(index) {
      const at = `rules[${index}]`;
      return this.problems.filter((p) => p.path === at || p.path.indexOf(`${at}.`) === 0).length;
    },
    noChannelFor(rule) { return !isOn(rule.sms.on) && !isOn(rule.email.on); },
    summaryOf(rule) {
      return ruleSummary(rule, {
        labelForCode: (c) => { const chip = chipsForCodes([c], this.codeOptions)[0]; return chip ? chip.code : c; },
        providerNameFor: (p) => { const o = this.providerOpts.find((x) => x.value === p); return o ? o.name : `Provider ${p}`; },
      });
    },
    providerFallbacks(rule) {
      const names = {};
      rule.providerNums.forEach((p) => { names[p] = `Provider ${p}`; });
      return names;
    },

    // ── the host contract ──
    /** A Save attempt: open every rule card that has a problem (a collapsed card would hide it) and report how many problems there are. */
    validate() {
      this.work.rules.forEach((rule, i) => { if (this.ruleIssues(i) > 0) this.$set(this.expanded, rule.id, true); });
      return this.errorCount;
    },
    /** Content only — ⛔ never `enabled` / `liveSince`. Never throws. */
    async save() {
      if (this.saveBusy) return { ok: false, message: 'Still saving — one moment.' };
      this.saveBusy = true;
      this.clearFailure();
      try {
        const res = await saveFormsSettingsSection({ auth: this.auth(), siteID: this.siteId, section: 'newPatient', value: newPatientSavePayload(this.work) });
        if (res && res.settings) this.adopt(res.settings);
        else this.markSaved();
        return { ok: true };
      } catch (e) {
        this.showFailure(e, 'Couldn’t save — please try again.');
        return { ok: false, message: this.switchError };
      } finally {
        this.saveBusy = false;
      }
    },
    reset() {
      this.work = clone(this.baseline);
      this.clearFailure();
      this.confirmOpen = false;
    },

    /** The working copy is what the server has now (a save whose answer carried no settings). */
    markSaved() {
      this.baseline = clone(this.work);
      this.baselineSig = newPatientSignature(this.work);
    },
    /** The server's settings become both the working copy and the baseline (so the tab is clean), and the switch as the server holds it. */
    adopt(settings) {
      const editable = newPatientEditable(settings);
      this.work = clone(editable);
      this.baseline = clone(editable);
      this.baselineSig = newPatientSignature(editable);
      this.server = flags(settings);
    },

    // ── the switch (the ONLY place `enabled` is sent) ──
    onRequestOn() {
      this.clearFailure();
      if (this.reasons.length > 0) return; // locked: the card says why
      this.confirmOpen = true;
    },
    async onConfirm() {
      this.confirmOpen = false;
      if (this.switchBusy || this.reasons.length > 0) return;
      this.clearFailure();
      this.switchBusy = true;
      try {
        // the office's unsaved edits go in the SAME request (the server's gate reads what it is about to store); an untouched tab sends just the switch
        const value = this.dirty ? Object.assign(newPatientSavePayload(this.work), { enabled: 'TRUE' }) : { enabled: 'TRUE' };
        const res = await saveFormsSettingsSection({ auth: this.auth(), siteID: this.siteId, section: 'newPatient', value });
        if (res && res.settings) this.adopt(res.settings); // liveSince is the SERVER's
        else { this.markSaved(); this.server = flags({ enabled: 'TRUE' }); }
        this.$emit('toast', 'New patient forms are on');
      } catch (e) {
        this.showFailure(e, 'Couldn’t turn this on. Please try again.');
      } finally {
        this.switchBusy = false;
      }
    },
    async onTurnOff() {
      if (this.switchBusy) return;
      this.clearFailure();
      this.switchBusy = true;
      try {
        const res = await saveFormsSettingsSection({ auth: this.auth(), siteID: this.siteId, section: 'newPatient', value: { enabled: 'FALSE' } });
        this.server = flags(res && res.settings ? res.settings : { enabled: 'FALSE' }); // only the switch changes: any unsaved edits stay unsaved
        this.$emit('toast', 'New patient forms are off');
      } catch (e) {
        this.showFailure(e, 'Couldn’t turn this off. Please try again.');
      } finally {
        this.switchBusy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.np { max-width: 1320px; margin: 0 auto; padding: 22px 32px 40px; display: flex; flex-direction: column; gap: 16px; }
.np-top { display: flex; flex-direction: column; gap: 12px; }
.np-cols { display: flex; flex-wrap: wrap; gap: 24px; align-items: flex-start; }
.np-left { flex: 1 1 540px; min-width: 0; display: flex; flex-direction: column; gap: 16px; }

.np-problems {
  margin: 0;
  padding: 10px 14px 10px 30px;
  border: 1px solid $pp-red-border;
  border-radius: 10px;
  background: $pp-red-subtle;
  font-size: 13.5px;
  font-weight: 600;
  color: $pfe-red-ink;
}
.np-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #d3e3f8;
  border-radius: 12px;
  background: #f1f6fd;
  font-size: 14px;
  line-height: 1.5;
  color: $pp-text-strong;

  > i { margin-top: 3px; color: $pfe-link-blue; }
}

.np-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.np-card-title { font-family: $pp-font-display; font-weight: 800; font-size: 16px; letter-spacing: -0.01em; }
.np-help { margin: 0; font-size: 13px; line-height: 1.5; color: $pfe-neutral-500; }
.np-sub { font-size: 13px; color: $pfe-neutral-500; }
.np-when { display: flex; align-items: center; gap: 10px; font-size: 14.5px; i { color: $pfe-neutral-500; font-size: 16px; } }
.np-msg-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; padding-top: 4px; }

.np-seg { display: inline-flex; align-self: flex-start; padding: 3px; border-radius: 999px; background: $pfe-neutral-100; gap: 2px; max-width: 100%; flex-wrap: wrap; }
.np-seg-btn {
  height: 32px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: $pfe-neutral-600;
  cursor: pointer;

  &--on { background: $pp-surface; color: $pp-text; box-shadow: $pp-shadow-raised; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }
}

.np-cards { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.np-rules { display: flex; flex-direction: column; gap: 16px; min-width: 0; }

.np-rule {
  min-width: 0;
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;

  &--open { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 14px; }
}
.np-rule-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px 10px 6px; }
.np-rule-sum {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 14px;
  color: $pfe-neutral-600;
  text-align: left;
  cursor: pointer;

  &:hover { background: $pfe-neutral-50; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }
}
.np-rule-chev { color: $pfe-neutral-400; font-size: 12px; }
.np-rule-label { font-weight: 800; color: $pp-text; white-space: nowrap; }
.np-rule-part { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.np-dot { color: $pfe-neutral-400; }
.np-rule-issue {
  flex: none;
  padding: 2px 8px;
  border-radius: 999px;
  background: $pp-red-subtle;
  border: 1px solid $pp-red-border;
  font-size: 11.5px;
  font-weight: 700;
  color: $pfe-red-ink;
}
.np-rule-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.np-rule-name {
  @include pfe-field(42px);
  flex: 1 1 220px;
  min-width: 0;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 700;

  &:focus { @include pfe-focus-ring; }
}
.np-rule-fields { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 16px; }
.np-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.np-field-label { font-size: 13px; font-weight: 700; }
.np-field-help { font-size: 12.5px; color: $pfe-neutral-500; }
.np-error { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 600; color: $pfe-red-ink; }

.np-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 4px 6px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 700;
  color: $pp-brand;
  cursor: pointer;

  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; border-radius: 6px; }
}
.np-icon-btn {
  flex: none;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: $pfe-neutral-500;
  font-size: 16px;
  cursor: pointer;

  &:hover { background: $pfe-neutral-100; color: $pp-text; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
}
.np-add { display: flex; }
.np-btn { @include pfe-button(secondary); }

.np-else { display: flex; flex-direction: column; gap: 12px; padding-top: 4px; }
.np-else-head { display: flex; flex-direction: column; gap: 2px; }

@media (max-width: 1100px) {
  .np-rule-fields { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 700px) {
  .np { padding: 18px 16px 32px; }
}
</style>
